import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, message } from 'antd';
import * as signalR from '@microsoft/signalr';

import { sendActivationAgain } from 'services/auth';
import useTranslation from 'customHooks/translate';

import Countdown from '../components/feature/countdown';
import HeaderSecondary from '../components/Header/headerSecondary';
import { useLocalStorage } from '../hooks/useLocalStorage';
import ContactUsForm from './Dialogs/ContactUsForm';

const CheckEmailPage = () => {
  const [countDown, setCountDown] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [currentDate, setCurrentDate] = useState<string>('');
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  const [dateInStorage, setDateInStorage] = useLocalStorage('date', '');
  const [emailInStorage] = useLocalStorage('email', '');
  const translation = useTranslation();

  const emailValue =
    typeof emailInStorage === 'object' && emailInStorage?.data
      ? emailInStorage.data
      : emailInStorage;
  const dateValue =
    typeof dateInStorage === 'object' && dateInStorage?.data
      ? dateInStorage.data
      : dateInStorage;

  const effectiveDate = currentDate || dateValue;

  useEffect(() => {
    if (emailValue) {
      const now = new Date();
      let needNewDate = false;

      if (!dateValue) {
        needNewDate = true;
      } else {
        const existingDate = new Date(dateValue);
        if (now >= existingDate) {
          needNewDate = true;
        }
      }

      if (needNewDate && !currentDate) {
        const expirationTime = new Date(now.getTime() + 5 * 60 * 1000);
        const dateString = expirationTime.toISOString();

        setCurrentDate(dateString);
        setDateInStorage(dateString);
      }
    }
  }, [emailValue, dateValue, currentDate, setDateInStorage]);

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl('https://38.242.208.6:88/notificationHub')
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    const startConnection = async () => {
      try {
        await connection.start();
        connection.on('ReceiveMessage', (response) => {
          if (response === 'Activation successful') {
            message.success(translation.messages.successActive);
            setIsActivated(true);
          }
        });
      } catch {}
    };

    startConnection();

    return () => {
      connection.stop();
    };
  }, []);

  if (isActivated) {
    return <Navigate to="/login" replace />;
  }

  if (!emailValue) {
    return <Navigate to="/not-found" replace />;
  }

  const handleSendActivationAgain = async () => {
    if (emailValue) {
      const payload = {
        email: emailValue,
      };
      try {
        await sendActivationAgain(payload);
        message.success(translation.checkEmail.TryAgainText);

        const currentTime = new Date();
        const expirationTime = new Date(currentTime.getTime() + 5 * 60 * 1000);
        const dateString = expirationTime.toISOString();

        setCurrentDate(dateString);
        setDateInStorage(dateString);
      } catch (error) {
        message.error('Failed to send activation email');
      }
    }
  };

  const dateForCountdown = effectiveDate ? new Date(effectiveDate) : new Date();

  return (
    <section className="box-border grid h-screen grid-cols-1 gap-[60px] overflow-hidden px-[24px] py-[24px]  md:pr-[24px] lg:grid-cols-2 xl:grid-cols-3 xl:gap-[120px] xl:pl-[156px]">
      <div className="col-span-1 flex flex-col gap-[96px]">
        <HeaderSecondary />
        <div className="flex flex-col gap-[24px]">
          <div className="flex flex-col gap-[12px]">
            <h1 className="text-2xl font-bold text-[#222] md:text-4xl">
              {translation.checkEmail.EmailCheckText}
            </h1>
            <span className="block font-markpro">
              {translation.checkEmail.DetailedTextPart1} {emailValue}{' '}
              {translation.checkEmail.DetailedTextPart2}.
            </span>
          </div>
          <Countdown
            date={dateForCountdown}
            countDown={countDown}
            setCountDown={setCountDown}
            setIsTimerRunning={setIsTimerRunning}
          />
          <Button
            onClick={handleSendActivationAgain}
            className={`${
              countDown > 0
                ? 'cursor-not-allowed underline underline-offset-4 opacity-50 md:text-charleston-green'
                : 'cursor-pointer text-[#6DCD90] hover:text-[#5BB87A]'
            } h-[44px] rounded-md !border-0 !bg-[#6DCE92] font-medium !text-white shadow-none transition-colors duration-200 hover:!border-0 hover:!bg-[#5EC082] hover:!text-white focus:!border-0 focus:!bg-[#6DCE92] focus:!text-white active:!border-0 active:!bg-[#5EC082] active:!text-white`}
            disabled={countDown > 0}
          >
            {translation.checkEmail.TryAgainLink}
          </Button>
          <div className="mb-8 flex items-center gap-x-1 font-markpro text-sm sm:mx-auto sm:justify-center sm:text-center">
            <div className="h-5 flex-shrink-0 self-start">
              <img src="assets/info-circle.svg" alt="" />
            </div>
            <p>
              {translation.checkEmail.ContactText}{' '}
              <button
                type="button"
                onClick={() => setIsContactFormOpen(true)}
                className="font-inherit cursor-pointer border-0 bg-transparent p-0 underline underline-offset-2"
              >
                {translation.checkEmail.ContactLink}
              </button>
            </p>
          </div>
        </div>
      </div>
      <div className="cols-span-1 hidden h-full overflow-hidden rounded-[24px] lg:block xl:col-span-2">
        <img
          src="assets/login-cover.svg"
          alt="login-cover"
          className="h-full w-full object-cover"
        />
      </div>
      <ContactUsForm
        open={isContactFormOpen}
        handleClose={() => setIsContactFormOpen(false)}
      />
    </section>
  );
};

export default CheckEmailPage;
